import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsStoresResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { Store, Wifi, WifiOff, Activity, ShoppingBag, DollarSign, Star, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CommonCardSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatRand } from '@/utility/helper';

interface StorePerformanceSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsStoresResponse['data'] | null;
    isLoading: boolean;
}


export const StorePerformanceSection: React.FC<StorePerformanceSectionProps> = ({ filters, data, isLoading }) => {
    
    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Total Stores', value: kpis.totalStores ?? 0, icon: <Store />, color: '#3B82F6' },
        { label: 'Active Stores', value: kpis.activeStores ?? 0, icon: <Activity />, color: '#10B981' },
        { label: 'Online Stores', value: kpis.onlineStores ?? 0, icon: <Wifi />, color: '#8B5CF6' },
        { label: 'Offline Stores', value: kpis.offlineStores ?? 0, icon: <WifiOff />, color: '#EF4444' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Store Performance</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=stores"
                    fileName="store_performance.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Revenue by Store" icon={<DollarSign />}>
                    <div className="h-[300px] w-full">
                        {!charts.revenueByStore || charts.revenueByStore.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={charts.revenueByStore} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `R${val/1000}k`} />
                                <YAxis type="category" dataKey="storeName" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }} width={120} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    formatter={(value: number) => [formatRand(value), 'Revenue']}
                                />
                                <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} barSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Orders by Store" icon={<ShoppingBag />}>
                    <div className="h-[300px] w-full">
                        {!charts.ordersByStore || charts.ordersByStore.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={charts.ordersByStore} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                <YAxis type="category" dataKey="storeName" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }} width={120} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar dataKey="orders" fill="#8B5CF6" radius={[0, 4, 4, 0]} barSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
                <SectionCard title="Best Performing Stores" className="overflow-x-auto">
                    <CommonTable 
                        columns={[
                            { key: 'storeName', header: 'Store Name', render: (row: any) => <div className="flex items-center gap-2"><Store className="w-4 h-4 text-primary" />{row.storeName}</div>, className: 'font-medium text-foreground' },
                            { key: 'orders', header: 'Orders', className: 'text-right', headerClassName: 'text-right' },
                            { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-medium text-success', headerClassName: 'text-right' },
                            { key: 'avgOrderValue', header: 'AOV', render: (row: any) => formatRand(row.avgOrderValue), className: 'text-right', headerClassName: 'text-right' },
                            { key: 'acceptanceRate', header: 'Acceptance', render: (row: any) => `${row.acceptanceRate}%`, className: 'text-right text-success', headerClassName: 'text-right' },
                            { key: 'cancellationRate', header: 'Cancel %', render: (row: any) => `${row.cancellationRate}%`, className: 'text-right text-destructive', headerClassName: 'text-right' },
                            { key: 'avgPrepTime', header: 'Prep Time', render: (row: any) => `${row.avgPrepTime}m`, className: 'text-right', headerClassName: 'text-right' },
                            { key: 'rating', header: 'Rating', render: (row: any) => <div className="flex items-center justify-end gap-1"><Star className="w-4 h-4 fill-amber-500 text-amber-500"/> {row.rating}</div>, className: 'text-right', headerClassName: 'text-right' }
                        ]}
                        data={tables.bestPerforming || []}
                    />
                </SectionCard>
                
                 <SectionCard title="Lowest Performing Stores" className="overflow-x-auto">
                    <CommonTable 
                        columns={[
                            { key: 'storeName', header: 'Store Name', render: (row: any) => <div className="flex items-center gap-2"><Store className="w-4 h-4 text-destructive" />{row.storeName}</div>, className: 'font-medium text-foreground' },
                            { key: 'orders', header: 'Orders', className: 'text-right', headerClassName: 'text-right' },
                            { key: 'revenue', header: 'Revenue', render: (row: any) => formatRand(row.revenue), className: 'text-right font-medium text-success', headerClassName: 'text-right' },
                            { key: 'avgOrderValue', header: 'AOV', render: (row: any) => formatRand(row.avgOrderValue), className: 'text-right', headerClassName: 'text-right' },
                            { key: 'acceptanceRate', header: 'Acceptance', render: (row: any) => `${row.acceptanceRate}%`, className: 'text-right text-warning', headerClassName: 'text-right' },
                            { key: 'cancellationRate', header: 'Cancel %', render: (row: any) => `${row.cancellationRate}%`, className: 'text-right text-destructive', headerClassName: 'text-right' },
                            { key: 'avgPrepTime', header: 'Prep Time', render: (row: any) => `${row.avgPrepTime}m`, className: 'text-right text-destructive', headerClassName: 'text-right' },
                            { key: 'rating', header: 'Rating', render: (row: any) => <div className="flex items-center justify-end gap-1"><Star className="w-4 h-4 fill-muted text-muted"/> {row.rating}</div>, className: 'text-right', headerClassName: 'text-right' }
                        ]}
                        data={tables.lowestPerforming || []}
                    />
                </SectionCard>
            </div>
        </div>
    );
};
